import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Av } from '../ui.jsx';

const ROLES = ['owner','admin','dispatcher','tech','csr','viewer'];
const ROLE_DESC = {
  owner: 'Full access. Cannot be removed.',
  admin: 'Manage team, settings, integrations.',
  dispatcher: 'Schedule jobs, assign techs, view all customers.',
  tech: 'See assigned jobs, complete them, collect payment.',
  csr: 'Customer-facing — answer messages, book appointments.',
  viewer: 'Read-only access to dashboard and reports.',
};
const ROLE_COLOR = { owner:G.purple, admin:G.cyan, dispatcher:G.blue, tech:G.green, csr:G.orange, viewer:G.textMuted };

export function Team({ user, toast }) {
  const [team, setTeam] = useState([]);
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editTech, setEditTech] = useState(null);
  const [permsFor, setPermsFor] = useState(null);
  const [permsCatalog, setPermsCatalog] = useState({ all:[], roleDefaults:{} });
  const [tab, setTab] = useState('users');

  useEffect(() => { api('/api/team/permissions/catalog').then(setPermsCatalog).catch(()=>{}); }, []);

  const load = async () => {
    try {
      const [t, tx] = await Promise.all([api('/api/team'), api('/api/techs')]);
      setTeam(t); setTechs(tx);
    } catch(e) { toast(e.message,'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const removeUser = async u => {
    if (u.role === 'owner') { toast('Cannot remove owner','warn'); return; }
    if (!confirm(`Permanently delete ${u.firstName} ${u.lastName||''}?\n\nThis removes the user account, their tech record (if any), and unlinks them from existing jobs/conversations.\n\nThis cannot be undone.`)) return;
    try { await api(`/api/team/${u.id}`, { method:'DELETE' }); load(); toast(`✓ ${u.firstName} deleted`); }
    catch(e) { toast(e.message,'error'); }
  };

  // Jump into a tech's portal view by impersonating their session. Server stashes
  // the originator so we can restore via /unjump-user. Full reload so the App
  // root re-reads /api/auth/user and routes into <TechApp/>.
  const jumpToUser = async u => {
    try {
      await api('/api/auth/jump-user', { method:'POST', body:{ userId: u.id }});
      window.location.reload();
    } catch(e) { toast(e.message, 'error'); }
  };

  const canManage = ['owner','admin','super_admin'].includes(user.role);

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Team</h1>
      {canManage && <Btn v="primary" onClick={()=>setEditing({ role:'tech', firstName:'', lastName:'', email:'', phone:'' })}>+ Invite Team Member</Btn>}
    </div>

    <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
      <Btn v={tab==='users'?'primary':'ghost'} sz="sm" onClick={()=>setTab('users')}>👥 Users ({team.length})</Btn>
      <Btn v={tab==='techs'?'primary':'ghost'} sz="sm" onClick={()=>setTab('techs')}>🔧 Field Techs ({techs.length})</Btn>
      {tab==='techs' && canManage && <Btn v="primary" sz="sm" onClick={()=>setEditTech({ _isNew:true, role:'Technician', status:'active', _fromUserId:'' })}>+ Add Field Tech</Btn>}
    </div>

    {loading ? <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner sz={32}/></div>
    : tab === 'users' ? <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:12 }}>
      {team.map(u => (
        <div key={u.id} style={{ ...styles.card }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
            <Av name={`${u.firstName} ${u.lastName||''}`} sz={48}/>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <strong>{u.firstName} {u.lastName}</strong>
                <Badge c={ROLE_COLOR[u.role]||G.textMuted} bg={(ROLE_COLOR[u.role]||G.textMuted)+'22'}>{u.role}</Badge>
                {u.status === 'suspended' && <Badge c={G.red} bg={G.redLight}>suspended</Badge>}
              </div>
              <div style={{ fontSize:13, color:G.textMuted, marginTop:4, wordBreak:'break-word' }}>{u.email}</div>
              {u.phone && <div style={{ fontSize:13, color:G.textMuted }}>{u.phone}</div>}
              {u.permissions && <div style={{ fontSize:11, color:G.textDim, marginTop:6 }}>
                {u.permissions.length} permissions{u.permissionsOverride && (u.permissionsOverride.grant?.length || u.permissionsOverride.revoke?.length) ? ' (custom)' : ''}
              </div>}
              {canManage && <div style={{ display:'flex', gap:6, marginTop:10, flexWrap:'wrap' }}>
                <Btn sz="xs" onClick={()=>setEditing(u)}>Edit</Btn>
                <Btn sz="xs" v="cyan" onClick={()=>setPermsFor(u)}>🔐 Permissions</Btn>
                {u.role === 'tech' && u.id !== user.id && <Btn sz="xs" v="purple" onClick={()=>jumpToUser(u)}>👁 Jump to portal</Btn>}
                {u.role !== 'owner' && u.id !== user.id && <Btn sz="xs" v="danger" onClick={()=>removeUser(u)}>Remove</Btn>}
              </div>}
            </div>
          </div>
        </div>
      ))}
    </div>
    : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(360px,1fr))', gap:12 }}>
      {techs.map(t => (
        <div key={t.id} onClick={()=>canManage && setEditTech(t)} role={canManage?'button':undefined} style={{...styles.card, cursor:canManage?'pointer':'default'}}>
          <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
            <Av name={t.name} sz={48}/>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                <strong>{t.name}</strong>
                <Badge c={G.cyan} bg={G.cyanLight}>{t.role}</Badge>
                <Badge c={G.green} bg={G.greenLight}>${t.hourlyRate||0}/hr</Badge>
              </div>
              <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{t.phone||'—'} · {t.email||'—'}</div>
              {(t.skills||[]).length > 0 && <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginTop:8 }}>
                {t.skills.map(s => <Badge key={s} c={G.purple} bg={G.purpleDim}>{s}</Badge>)}
              </div>}
              {(t.serviceArea||[]).length > 0 && <div style={{ fontSize:11, color:G.textMuted, marginTop:6 }}>📍 {t.serviceArea.join(' · ')}</div>}
              {t.workingHours && Object.keys(t.workingHours).length > 0 && <div style={{ fontSize:11, color:G.textMuted, marginTop:4 }}>🕐 Working {Object.entries(t.workingHours).filter(([,v])=>v).map(([d])=>d).join('·')}</div>}
            </div>
          </div>
        </div>
      ))}
      {techs.length === 0 && <div style={{ ...styles.card, padding:32, color:G.textMuted, textAlign:'center', gridColumn:'1 / -1' }}>No techs yet. Invite a team member with role "tech" or "dispatcher" to add one automatically.</div>}
    </div>}

    <UserModal user={editing} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Saved'); }}/>
    {editTech?._isNew
      ? <NewTechModal users={team} existingTechs={techs} onClose={()=>setEditTech(null)} onSaved={()=>{ load(); setEditTech(null); toast('Tech added'); }} toast={toast}/>
      : <TechModal tech={editTech} accountIndustry={user?.account?.industry} onClose={()=>setEditTech(null)} onSaved={()=>{ load(); setEditTech(null); toast('Tech updated'); }}/>
    }
    <PermissionsModal user={permsFor} catalog={permsCatalog} onClose={()=>setPermsFor(null)} onSaved={()=>{ load(); setPermsFor(null); toast('Permissions updated'); }}/>
  </div>;
}

// NewTechModal — add a tech by either picking an existing user (someone with
// a login already) OR creating a brand-new tech. The "pick existing user" mode
// is what the owner asked for so they don't have to type the name/email/phone
// of someone who's already on the team.
function NewTechModal({ users, existingTechs, onClose, onSaved, toast }) {
  const [mode, setMode] = useState('existing');
  const [userId, setUserId] = useState('');
  const [f, setF] = useState({ name:'', role:'Technician', phone:'', email:'', hourlyRate:0 });
  const [busy, setBusy] = useState(false);
  const existingEmails = new Set((existingTechs||[]).map(t => (t.email||'').toLowerCase()).filter(Boolean));
  // Only show users that aren't already techs and aren't read-only viewers.
  const pickable = (users||[]).filter(u => u.status !== 'suspended' && !existingEmails.has((u.email||'').toLowerCase()));
  const submit = async () => {
    setBusy(true);
    try {
      let body;
      if (mode === 'existing') {
        const u = users.find(x => x.id === userId);
        if (!u) { toast('Pick a user first', 'error'); setBusy(false); return; }
        body = { name: `${u.firstName||''} ${u.lastName||''}`.trim() || u.email, email: u.email, phone: u.phone || null, role: 'Technician', status: 'active' };
      } else {
        if (!f.name) { toast('Name is required', 'error'); setBusy(false); return; }
        body = { ...f };
      }
      const res = await api('/api/techs', { method:'POST', body });
      if (res.tempPassword) {
        alert(`Tech created. Temporary password for ${res.loginEmail}: ${res.tempPassword}\n\nShare this so they can log into the field tech portal. They can change it after first login.`);
      }
      onSaved();
    } catch(e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <Modal open onClose={onClose} width={580}>
    <h2 style={{ margin:'0 0 14px 0' }}>+ Add Field Tech</h2>
    <div style={{ display:'flex', gap:8, marginBottom:14, background:G.input, padding:4, borderRadius:8 }}>
      <Btn sz="sm" v={mode==='existing'?'primary':'ghost'} onClick={()=>setMode('existing')} style={{ flex:1 }}>👤 Pick existing user</Btn>
      <Btn sz="sm" v={mode==='new'?'primary':'ghost'} onClick={()=>setMode('new')} style={{ flex:1 }}>✨ Create new</Btn>
    </div>
    {mode === 'existing' ? <>
      <Field label="Pick a user from your team">
        <Select value={userId} onChange={e=>setUserId(e.target.value)}>
          <option value="">— Select a user —</option>
          {pickable.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName} · {u.role}{u.email ? ` · ${u.email}` : ''}</option>)}
        </Select>
      </Field>
      <div style={{ fontSize:12, color:G.textMuted, lineHeight:1.55 }}>
        Creates a tech record linked to this user's email so jobs assigned to them sync to their Google Calendar (if connected) and they can use the field-tech portal.
        After creation, click the tech card to fill in hourly rate, skills, working hours, service area.
      </div>
    </> : <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Name *" style={{ gridColumn:'1/-1' }}><Input value={f.name} onChange={e=>setF({...f, name:e.target.value})}/></Field>
      <Field label="Role"><Input value={f.role} onChange={e=>setF({...f, role:e.target.value})}/></Field>
      <Field label="Hourly Rate"><Input type="number" value={f.hourlyRate||0} onChange={e=>setF({...f, hourlyRate:Number(e.target.value)||0})}/></Field>
      <Field label="Email (creates login)"><Input type="email" value={f.email} onChange={e=>setF({...f, email:e.target.value})}/></Field>
      <Field label="Phone"><Input value={f.phone} onChange={e=>setF({...f, phone:e.target.value})}/></Field>
    </div>}
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
      <Btn v="primary" onClick={submit} disabled={busy || (mode==='existing' ? !userId : !f.name)}>{busy ? <Spinner sz={14}/> : 'Add Tech'}</Btn>
    </div>
  </Modal>;
}

function TechModal({ tech, onClose, onSaved, accountIndustry }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(tech || {}); }, [tech]);
  if (!tech) return null;
  const updateSkill = (s, on) => {
    const cur = new Set(f.skills||[]);
    if (on) cur.add(s); else cur.delete(s);
    setF({ ...f, skills: Array.from(cur) });
  };
  // Skill catalog per vertical. When account.industry is set, techs on that
  // account only see relevant skills — an Electric Medic account never shows
  // "HVAC Install" or "Drain Cleaning" as pickable skills.
  const SKILLS_BY_INDUSTRY = {
    Electrical: ['Panel Upgrade','Panel Replacement','Service Call','Rewire','New Circuit','EV Charger','Generator Install','Lighting Install','Outlet/Switch','Ceiling Fan','Smoke/CO Detector','Surge Protection','Whole-Home Inspection','Troubleshooting','Commercial','Residential'],
    HVAC: ['HVAC Install','HVAC Repair','HVAC Maintenance','Furnace','Mini Split','Refrigeration','Diagnostic','Commercial','Residential'],
    Plumbing: ['Plumbing','Drain Cleaning','Water Heater','Repipe','Sewer','Leak Detection','Gas Line','Fixture Install','Commercial','Residential'],
    Roofing: ['Inspection','Leak Repair','Storm Damage','Full Replacement','Ventilation','Gutter Install','Commercial','Residential'],
  };
  const SKILLS = SKILLS_BY_INDUSTRY[accountIndustry] || ['HVAC Install','HVAC Repair','HVAC Maintenance','Furnace','Mini Split','Plumbing','Electrical','Roofing','Drain Cleaning','Generator','EV Charger','Diagnostic','Refrigeration','Commercial','Residential'];
  const days = [['mon','Mon'],['tue','Tue'],['wed','Wed'],['thu','Thu'],['fri','Fri'],['sat','Sat'],['sun','Sun']];

  const save = async () => {
    try { await api(`/api/techs/${f.id}`, { method:'PATCH', body: f }); onSaved(); }
    catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={680}>
    <h2 style={{ margin:'0 0 14px 0' }}>{f.name}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Name"><Input value={f.name||''} onChange={e=>setF({...f,name:e.target.value})}/></Field>
      <Field label="Role"><Input value={f.role||''} onChange={e=>setF({...f,role:e.target.value})}/></Field>
      <Field label="Phone"><Input value={f.phone||''} onChange={e=>setF({...f,phone:e.target.value})}/></Field>
      <Field label="Email"><Input value={f.email||''} onChange={e=>setF({...f,email:e.target.value})}/></Field>
      <Field label="Hourly Rate"><Input type="number" step="0.01" value={f.hourlyRate||0} onChange={e=>setF({...f,hourlyRate:Number(e.target.value)})}/></Field>
      <Field label="Seniority (years)"><Input type="number" step="0.5" value={f.seniorityYears||0} onChange={e=>setF({...f,seniorityYears:Number(e.target.value)})}/></Field>
      <Field label="Service Area (cities, comma-sep)" style={{gridColumn:'1/-1'}}>
        <Input value={(f.serviceArea||[]).join(', ')} onChange={e=>setF({...f, serviceArea: e.target.value.split(',').map(s=>s.trim()).filter(Boolean)})}/>
      </Field>
      <Field label="Home Base Address" style={{gridColumn:'1/-1'}}><Input value={f.homeBaseAddress||''} onChange={e=>setF({...f,homeBaseAddress:e.target.value})}/></Field>

      <div style={{ gridColumn:'1/-1', borderTop:`1px solid ${G.border}`, paddingTop:14, marginTop:4 }}>
        <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>💰 Fully-Loaded Cost (used in live job margin)</div>
        <div style={{ fontSize:12, color:G.textMuted, marginBottom:10, lineHeight:1.5 }}>
          Base wage × (1 + tax + benefits + overhead) = true cost per hour. Drives live margin tickers + drift alerts.
        </div>
      </div>
      <Field label="Base wage ($/hr)"><Input type="number" step="0.01" value={(f.baseWageCents||0)/100} onChange={e=>setF({...f, baseWageCents: Math.round(Number(e.target.value)*100)})}/></Field>
      <Field label="Employer tax %"><Input type="number" step="0.5" value={f.employerTaxPct ?? 8} onChange={e=>setF({...f, employerTaxPct: Number(e.target.value)})}/></Field>
      <Field label="Benefits %"><Input type="number" step="0.5" value={f.benefitsPct ?? 12} onChange={e=>setF({...f, benefitsPct: Number(e.target.value)})}/></Field>
      <Field label="Overhead %"><Input type="number" step="0.5" value={f.overheadPct ?? 20} onChange={e=>setF({...f, overheadPct: Number(e.target.value)})}/></Field>
      <Field label="OT multiplier"><Input type="number" step="0.05" value={f.otMultiplier ?? 1.5} onChange={e=>setF({...f, otMultiplier: Number(e.target.value)})}/></Field>
      <Field label="True hourly cost">
        <div style={{ padding:'10px 12px', background:G.input, borderRadius:8, fontFamily:'monospace', fontSize:14, color:G.purple, fontWeight:700 }}>
          ${(((f.baseWageCents||0)/100) * (1 + (Number(f.employerTaxPct||8) + Number(f.benefitsPct||12) + Number(f.overheadPct||20))/100)).toFixed(2)}/hr
        </div>
      </Field>
      <Field label="Smart Routing" style={{gridColumn:'1/-1'}}>
        <label style={{ display:'flex', gap:8, alignItems:'center', fontSize:13, color:G.textMuted, cursor:'pointer' }}>
          <input type="checkbox" checked={!!f.autoRouteOptimize} onChange={e=>setF({...f, autoRouteOptimize: e.target.checked})}/>
          <span><strong style={{color:G.text}}>Auto-optimize this tech's day every morning.</strong> System re-sequences their jobs to minimize drive time — preserves the first start time, shifts later jobs 30 min apart.</span>
        </label>
      </Field>
      <Field label="Map Color" style={{gridColumn:'1/-1'}}>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {['#22c55e','#a78bfa','#22d3ee','#f97316','#3b82f6','#ef4444','#eab308','#ec4899','#10b981','#8b5cf6'].map(c => (
            <button key={c} onClick={()=>setF({...f, color:c})} style={{
              width:28, height:28, borderRadius:14, background:c, cursor:'pointer',
              border: f.color === c ? '3px solid #fff' : '3px solid transparent',
              boxShadow: f.color === c ? `0 0 0 2px ${c}` : 'none',
            }}/>
          ))}
          {f.color && <button onClick={()=>setF({...f, color:null})} style={{ background:'none', border:'none', color:G.textMuted, fontSize:11, cursor:'pointer' }}>Clear</button>}
        </div>
      </Field>
    </div>

    <Field label="Skills (used by smart dispatch)">
      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
        {SKILLS.map(s => {
          const on = (f.skills||[]).includes(s);
          return <Badge key={s} c={on?G.purple:G.textMuted} bg={on?G.purpleDim:G.input} onClick={()=>updateSkill(s, !on)}>
            <span style={{ cursor:'pointer', padding:'2px 8px' }}>{on?'✓ ':''}{s}</span>
          </Badge>;
        })}
      </div>
    </Field>

    <Field label="Working Hours">
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4 }}>
        {days.map(([k,l]) => {
          const v = (f.workingHours||{})[k] || '';
          return <div key={k} style={{ ...styles.card, padding:6, textAlign:'center' }}>
            <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, marginBottom:4 }}>{l}</div>
            <Input value={v||''} onChange={e=>setF({...f, workingHours:{...(f.workingHours||{}), [k]: e.target.value || null }})} placeholder="07:00-17:00" style={{ fontSize:11, padding:'4px 6px' }}/>
          </div>;
        })}
      </div>
    </Field>

    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}

function PermissionsModal({ user, catalog, onClose, onSaved }) {
  const [grant, setGrant] = useState([]);
  const [revoke, setRevoke] = useState([]);
  useEffect(() => {
    if (user) {
      setGrant(user.permissionsOverride?.grant || []);
      setRevoke(user.permissionsOverride?.revoke || []);
    }
  }, [user]);
  if (!user) return null;
  const defaults = catalog.roleDefaults?.[user.role] || [];
  const all = catalog.all || [];

  const togglePerm = (p) => {
    const isDefault = defaults.includes(p);
    const isGrantedOverride = grant.includes(p);
    const isRevokedOverride = revoke.includes(p);
    // 4 states cycle: default → if default-on revoke it; if default-off grant it
    if (isDefault) {
      if (isRevokedOverride) setRevoke(revoke.filter(x=>x!==p)); // back to default-on
      else setRevoke([...revoke, p]); // revoke
    } else {
      if (isGrantedOverride) setGrant(grant.filter(x=>x!==p)); // back to default-off
      else setGrant([...grant, p]); // grant
    }
  };
  const stateOf = (p) => {
    const isDefault = defaults.includes(p);
    if (isDefault && revoke.includes(p)) return 'revoked';
    if (!isDefault && grant.includes(p)) return 'granted';
    return isDefault ? 'allowed' : 'denied';
  };

  const save = async () => {
    try {
      const permissions = (grant.length || revoke.length) ? { grant, revoke } : null;
      await api(`/api/team/${user.id}`, { method:'PATCH', body:{ permissions }});
      onSaved();
    } catch(e) { alert(e.message); }
  };

  return <Modal open onClose={onClose} width={620}>
    <h2 style={{ margin:'0 0 4px 0' }}>🔐 Permissions: {user.firstName} {user.lastName}</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
      Role: <Badge c={G.purple} bg={G.purpleDim}>{user.role}</Badge> · {defaults.length} default permissions. Click any permission to grant or revoke vs. role default.
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
      {all.map(p => {
        const s = stateOf(p);
        const c = s==='allowed'?G.green : s==='granted'?G.cyan : s==='revoked'?G.red : G.textMuted;
        const label = s==='allowed'?'✓ allowed (role default)' : s==='granted'?'+ granted' : s==='revoked'?'× revoked' : '— denied (role default)';
        return <div key={p} onClick={()=>togglePerm(p)} style={{ ...styles.card, padding:8, cursor:'pointer', borderColor: c+'66' }}>
          <div style={{ fontSize:13, fontWeight:600 }}>{p}</div>
          <div style={{ fontSize:11, color:c, marginTop:2 }}>{label}</div>
        </div>;
      })}
    </div>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save Permissions</Btn>
    </div>
  </Modal>;
}

function UserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [tempPw, setTempPw] = useState('');
  useEffect(() => { setForm(user || {}); setTempPw(''); }, [user]);
  if (!user) return null;
  const isNew = !form.id;

  const save = async () => {
    try {
      let result;
      if (isNew) result = await api('/api/team', { method:'POST', body: form });
      else result = await api(`/api/team/${form.id}`, { method:'PATCH', body: form });
      if (result.tempPassword) { setTempPw(result.tempPassword); }
      else onSaved();
    } catch(e) { alert(e.message); }
  };

  if (tempPw) {
    return <Modal open onClose={onSaved} width={460}>
      <h2 style={{ margin:'0 0 8px 0' }}>✅ Team Member Added</h2>
      <div style={{ color:G.textMuted, marginBottom:18 }}>Share this temporary password with them. They'll be prompted to change it on first login.</div>
      <div style={{ ...styles.card, padding:20, textAlign:'center', background:G.input }}>
        <div style={{ fontSize:11, color:G.textMuted, marginBottom:6, textTransform:'uppercase', letterSpacing:'.05em' }}>Temporary Password</div>
        <div style={{ fontSize:22, fontWeight:800, fontFamily:'monospace', color:G.green, letterSpacing:'.05em' }}>{tempPw}</div>
      </div>
      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:18 }}>
        <Btn v="primary" onClick={onSaved}>Got it</Btn>
      </div>
    </Modal>;
  }

  return <Modal open onClose={onClose} width={520}>
    <h2 style={{ margin:'0 0 18px 0' }}>{isNew?'Invite Team Member':`Edit ${form.firstName}`}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="First Name *"><Input value={form.firstName||''} onChange={e=>setForm({...form,firstName:e.target.value})}/></Field>
      <Field label="Last Name"><Input value={form.lastName||''} onChange={e=>setForm({...form,lastName:e.target.value})}/></Field>
      <Field label="Email *"><Input type="email" value={form.email||''} onChange={e=>setForm({...form,email:e.target.value})}/></Field>
      <Field label="Phone"><Input value={form.phone||''} onChange={e=>setForm({...form,phone:e.target.value})}/></Field>
      <Field label="Role" hint={ROLE_DESC[form.role]}>
        <Select value={form.role||'tech'} onChange={e=>setForm({...form,role:e.target.value})}>
          {ROLES.map(r => <option key={r}>{r}</option>)}
        </Select>
      </Field>
      <Field label={isNew?'Initial Password (auto-gen if blank)':'Reset Password'}>
        <Input type="password" value={form.password||''} onChange={e=>setForm({...form,password:e.target.value})} autoComplete="new-password"/>
      </Field>
    </div>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>{isNew?'Send Invite':'Save'}</Btn>
    </div>
  </Modal>;
}
